import type { RepeatIntervalUnit } from '@fieldops/shared'
import { addressLine } from '../../lib/format.js'
import type { RouterOutputs } from '../../lib/trpc.js'

type AgreementPage = RouterOutputs['agreements']['byId']
export type AgreementRow = AgreementPage['schedules'][number]
type SiteRef = AgreementPage['clientSites'][number] | NonNullable<AgreementRow['site']>

/** The agreement page read site by site, or service by service. */
export type GroupBy = 'site' | 'service'
/** Months with a dot for each visit, a row of figures to edit, or the next twelve weeks as a worklist. */
export type Look = 'timeline' | 'table' | 'coming'

/**
 * What the Show chips narrow the page to, each read from what the server sent
 * about the service (`agreements.byId`): `soon` a visit in the next eight
 * weeks, `job` a next visit inside the lead with no job, `overdue` a visit
 * gone by with no job, `noprice` a service on it with no price, `paused`
 * paused at its site.
 */
export type Show = 'all' | 'soon' | 'job' | 'overdue' | 'noprice' | 'paused'

export const SHOWS: ReadonlyArray<{ value: Show; label: string; tone?: 'red' | 'amber' }> = [
  { value: 'all', label: 'All' },
  { value: 'soon', label: 'Due in 8 weeks' },
  { value: 'job', label: 'Needs a job' },
  { value: 'overdue', label: 'Overdue', tone: 'red' },
  { value: 'noprice', label: 'No price', tone: 'amber' },
  { value: 'paused', label: 'Paused' },
]

export function isShow(value: string | null): value is Show {
  return SHOWS.some((entry) => entry.value === value)
}

/** Whether a service is one the chip asks for. */
export function shows(row: AgreementRow, show: Show): boolean {
  switch (show) {
    case 'soon':
      return row.dueSoon > 0
    case 'job':
      return row.needsJob
    case 'overdue':
      return row.overdue > 0
    case 'noprice':
      return row.services.some((service) => !service.priceIncTax)
    case 'paused':
      return row.stoppedAt !== null
    default:
      return true
  }
}

/** What narrows the page besides the grouping: the chip, the service picked and the search. */
export type Narrowing = { show: Show; service: string | null; term: string }

export const narrowed = ({ show, service, term }: Narrowing) => show !== 'all' || service !== null || term.trim() !== ''

export type AgreementGroup = {
  key: string
  name: string
  /** Under the name: a named site's address, or how many sites have the service. */
  sub: string | null
  /** By site, the site. */
  siteId: string | null
  /** By service, the site's own name, which renaming edits. */
  siteName: string | null
  /** By service, the one service every row is, when it is one. Add sites needs it. */
  serviceTypeId: string | null
  rows: AgreementRow[]
  aYear: number
}

/**
 * A site as the page names it: its own name, else its building's — a
 * council's building is a client of its own (ADR 026) — else its address.
 */
export function siteTitle(site: SiteRef | null | undefined, clientId: string): { name: string; address: string | null } {
  const owner = site && site.client.id !== clientId ? site.client.name : null
  const name = site?.name ?? owner ?? addressLine(site?.address)
  return { name, address: site && (site.name || owner) ? addressLine(site.address) : null }
}

export const serviceName = (row: AgreementRow) => row.services.map((service) => service.name).join(', ') || 'No service'

const serviceKey = (row: AgreementRow) =>
  row.services
    .map((service) => service.serviceTypeId)
    .sort()
    .join('+') || 'none'

/** A visit still to happen: made and not finished, or not made yet. */
const COMING = new Set(['CREATED', 'OVERDUE', 'NEXT', 'PLANNED'])

/** The day of the row's next visit: its booking when it has a job, else when it is due. */
export function nextOn(row: AgreementRow): string | null {
  const next = row.slots.find((slot) => COMING.has(slot.state))
  return next ? (next.job?.bookedOn ?? next.dueOn) : null
}

function matches(row: AgreementRow, term: string, clientId: string): boolean {
  if (!term) return true
  const site = siteTitle(row.site, clientId)
  const text = [site.name, site.address, addressLine(row.site?.address), serviceName(row)].join(' ').toLowerCase()
  return term
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => text.includes(word))
}

/**
 * The page's rows, narrowed by the chip, the service and the search, and grouped. By site keeps the
 * order the services were added in; by service is alphabetical, and so are
 * the sites inside each.
 */
export function groupRows(
  page: AgreementPage,
  by: GroupBy,
  { show, service, term }: Narrowing,
): AgreementGroup[] {
  const clientId = page.client.id
  const rows = page.schedules.filter(
    (row) =>
      shows(row, show) &&
      (service === null || row.services.some((entry) => entry.serviceTypeId === service)) &&
      matches(row, term.trim(), clientId),
  )
  const buckets = new Map<string, AgreementRow[]>()
  for (const row of rows) {
    const key = by === 'site' ? row.siteId : serviceKey(row)
    buckets.set(key, [...(buckets.get(key) ?? []), row])
  }

  const groups = [...buckets.entries()].map(([key, members]): AgreementGroup => {
    const first = members[0]!
    const aYear = members.reduce((sum, row) => sum + row.aYear, 0)
    if (by === 'site') {
      const site = siteTitle(first.site, clientId)
      return {
        key,
        name: site.name,
        sub: site.address,
        siteId: first.siteId,
        siteName: first.site?.name ?? null,
        serviceTypeId: null,
        rows: members,
        aYear,
      }
    }
    const sites = new Set(members.map((row) => row.siteId)).size
    return {
      key,
      name: serviceName(first),
      sub: `${sites} site${sites === 1 ? '' : 's'}`,
      siteId: null,
      siteName: null,
      serviceTypeId: first.services.length === 1 ? first.services[0]!.serviceTypeId : null,
      rows: [...members].sort((a, b) =>
        siteTitle(a.site, clientId).name.localeCompare(siteTitle(b.site, clientId).name),
      ),
      aYear,
    }
  })
  return by === 'service' ? groups.sort((a, b) => a.name.localeCompare(b.name)) : groups
}

export type PlanLine = AgreementPage['plan'][number]

/** `scheduleId:sequence`, the way the page names one visit. */
export const visitKey = (visit: { scheduleId: string; sequence: number }) => `${visit.scheduleId}:${visit.sequence}`

/** The visits of a plan line: the one it is made from first, then the rest. */
export const visitsOf = (line: PlanLine) => [{ scheduleId: line.scheduleId, sequence: line.sequence }, ...line.with]

/**
 * Which job each visit of the term is on, from the server's `plan`. The grid
 * draws visits on one job as joined, and a visit's popup makes that job.
 * The grouping is the server's (`agreementWorklist`); this only indexes it.
 */
export function planByVisit(page: Pick<AgreementPage, 'plan'>): ReadonlyMap<string, PlanLine> {
  const index = new Map<string, PlanLine>()
  for (const line of page.plan) {
    for (const visit of visitsOf(line)) index.set(visitKey(visit), line)
  }
  return index
}

export type FrequencyValue = { intervalCount: number; intervalUnit: RepeatIntervalUnit }

export const frequencyKey = (interval: FrequencyValue) => `${interval.intervalCount}-${interval.intervalUnit}`

export function frequencyOf(key: string): FrequencyValue {
  const [count, unit] = key.split('-') as [string, RepeatIntervalUnit]
  return { intervalCount: Number(count), intervalUnit: unit }
}

function mostCommon<T>(values: readonly T[], keyOf: (value: T) => string): T | undefined {
  const counts = new Map<string, { value: T; n: number }>()
  for (const value of values) {
    const key = keyOf(value)
    const entry = counts.get(key) ?? { value, n: 0 }
    entry.n += 1
    counts.set(key, entry)
  }
  return [...counts.values()].sort((a, b) => b.n - a.n)[0]?.value
}

/** What most of a service's sites have, for the next site it is added to. */
export function commonRule(rows: readonly AgreementRow[]): FrequencyValue & { priceIncTax: number | null; firstDueOn: string | null } {
  const frequency = mostCommon(rows.map((row) => ({ intervalCount: row.intervalCount, intervalUnit: row.intervalUnit })), frequencyKey)
  const price = mostCommon(rows.map((row) => row.services[0]?.priceIncTax ?? null), String)
  const first = mostCommon(rows.map((row) => row.firstDueOn), String)
  return {
    intervalCount: frequency?.intervalCount ?? 3,
    intervalUnit: frequency?.intervalUnit ?? 'MONTH',
    priceIncTax: price ?? null,
    firstDueOn: first ?? null,
  }
}
